/**
 * «Где видна» — выбор НЕСКОЛЬКИХ областей (AK-570/AK-551).
 *
 * Решение founder 10.08, дословно: «или весь холдинг, или кластер(ы) +
 * отдельные хозяйства вне их». Селект с одной строкой такого не выражал:
 * запись можно было показать либо всем, либо ровно одному кластеру, либо
 * ровно одному хозяйству.
 *
 * Форма — иерархический пикер аналитики (#716): холдинг → кластеры →
 * хозяйства. Дерево строим тем же `buildScopeTree`, что и общий
 * `ScopeTreeView`, но семантика выбора здесь ДРУГАЯ (множественная, с
 * галочками), поэтому это отдельный компонент, а не флаг у общего: смешав
 * «выбрать один scope сессии» и «отметить области видимости» в одном
 * компоненте, мы бы получили ветвление на каждой строке.
 *
 * Правило вложенности: отмеченный кластер ПОКРЫВАЕТ свои хозяйства — их
 * галочки становятся неактивными и подписаны «входит в кластер». Иначе человек
 * отмечает и кластер, и хозяйство внутри него, а потом гадает, что это значит.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { buildScopeTree, ScopeIcon, type ScopeInfo } from '@/components/scope/ScopeTree';
import { cn } from '@/lib/utils';
import type { CropScope } from '@/lib/reference/tenantCrops';

interface Props {
  available: readonly ScopeInfo[];
  value: CropScope[];
  onChange: (next: CropScope[]) => void;
  disabled?: boolean;
  /** Дерево ещё едет с сервера — пустота и «ничего не нашлось» это разные вещи. */
  loading?: boolean;
  /**
   * Ключ записи, которую сейчас правят. Меняется — режим пересчитывается от
   * её данных. Панель живёт дольше, чем одна запись (карточка переоткрывается
   * на месте), и без этого следующая запись открылась бы в режиме предыдущей.
   */
  resetKey?: string;
  /** id подписи «Где видна» — группа связывается с ней для скринридера. */
  labelledBy?: string;
}

function has(value: CropScope[], level: 'cluster' | 'farm', id: string): boolean {
  return value.some((s) => s.scope_level === level && s.scope_ref_id === id);
}

/** Режим по данным записи: пусто или холдинг-строка → «весь холдинг». */
function modeOf(value: CropScope[]): 'holding' | 'picked' {
  return value.length === 0 || value.some((s) => s.scope_level === 'holding') ? 'holding' : 'picked';
}

export function ScopeMembershipPicker({
  available,
  value,
  onChange,
  disabled,
  loading,
  resetKey,
  labelledBy,
}: Props) {
  const [search, setSearch] = useState('');

  /**
   * Объекты без id из выбора выкидываем СРАЗУ: дальше по коду id идёт в
   * `scope_ref_id`, и «область с пустым id» уехала бы на сервер как реальная.
   */
  const scopes = useMemo(
    () => available.filter((s) => s.scope_type === 'company' || Boolean(s.scope_id)),
    [available],
  );
  const tree = useMemo(() => buildScopeTree(scopes, search), [scopes, search]);

  /**
   * Режим ВЫВОДИТСЯ ИЗ ДАННЫХ, а сверху лежит липкий override.
   *
   * Две ловушки, обе уже сработали:
   *
   * 1. Выводить режим только из набора нельзя: переключаясь в «выбранные»,
   *    человек начинает с пустого набора, родитель кладёт `[]`, и режим прыгал
   *    бы обратно в «весь холдинг» — головная фича была недостижима вовсе.
   * 2. Снимать режим снапшотом в `useState(() => …)` тоже нельзя: карточка
   *    монтирует панель с `value = []` и кладёт реальные области СВОИМ
   *    эффектом, а эффекты ребёнка выполняются раньше родительских. Снапшот
   *    успевал зафиксировать «холдинг» до приезда данных, и запись, суженная
   *    до кластера, навсегда открывалась как «Весь холдинг» — без дерева,
   *    без галочек, и единственный путь к ним стирал текущий выбор.
   *
   * Отсюда: `picked`, если он выводится из данных, ИЛИ если человек сам
   * переключил режим (`stickyPicked`) — единственное состояние, которое из
   * данных не выводится, это «выбранные с пока пустым набором».
   */
  const [stickyPicked, setStickyPicked] = useState(false);

  // Открыли другую запись — липкость снимаем, режим снова читается от данных.
  useEffect(() => {
    setStickyPicked(false);
  }, [resetKey]);

  const mode: 'holding' | 'picked' =
    stickyPicked || modeOf(value) === 'picked' ? 'picked' : 'holding';
  const holding = mode === 'holding';
  const checkedClusters = useMemo(
    () =>
      new Set(
        value
          .filter((s) => s.scope_level === 'cluster' && s.scope_ref_id)
          .map((s) => s.scope_ref_id as string),
      ),
    [value],
  );

  function toggle(level: 'cluster' | 'farm', id: string, on: boolean) {
    if (!id) return;
    // Снятие ПОСЛЕДНЕЙ галочки оставляет пустой набор — без липкости режим
    // уронило бы обратно в «весь холдинг» прямо под руками.
    setStickyPicked(true);
    const rest = value.filter(
      (s) => s.scope_level !== 'holding' && !(s.scope_level === level && s.scope_ref_id === id),
    );
    let next = on ? [...rest, { scope_level: level, scope_ref_id: id }] : rest;
    if (level === 'cluster' && on) {
      // Кластер покрывает свои хозяйства — их отдельные отметки лишние.
      //
      // Считаем ИЗ ПОЛНОГО списка, а не из `tree`: дерево отфильтровано
      // поиском, и при активном поиске хозяйства кластера в него не попадают —
      // их точечные отметки пережили бы отметку кластера, и на сервер уехало бы
      // «кластер + хозяйство внутри него», то есть ровно то состояние, которое
      // этот компонент обещает не допускать.
      const inside = new Set(
        scopes
          .filter((s) => s.scope_type === 'farm' && s.parent_cluster_id === id)
          .map((s) => s.scope_id),
      );
      next = next.filter((s) => !(s.scope_level === 'farm' && inside.has(s.scope_ref_id ?? '')));
    }
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-3" role="group" aria-labelledby={labelledBy}>
      <RadioGroup
        className="flex flex-col gap-2"
        value={mode}
        onValueChange={(v) => {
          const next = v === 'holding' ? 'holding' : 'picked';
          setStickyPicked(next === 'picked');
          onChange(next === 'holding' ? [{ scope_level: 'holding', scope_ref_id: null }] : []);
        }}
        disabled={disabled}
      >
        <label
          htmlFor="scope-mode-holding"
          className="flex items-center gap-3 rounded-md border border-border p-3 hover:bg-accent"
        >
          <RadioGroupItem id="scope-mode-holding" value="holding" />
          <span className="text-sm">Весь холдинг</span>
        </label>
        <label
          htmlFor="scope-mode-picked"
          className="flex items-center gap-3 rounded-md border border-border p-3 hover:bg-accent"
        >
          <RadioGroupItem id="scope-mode-picked" value="picked" />
          <span className="text-sm">Выбранные кластеры и хозяйства</span>
        </label>
      </RadioGroup>

      {!holding && (
        <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Поиск хозяйства или кластера"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              // Пустой список во время загрузки читается как «у вас ничего нет»
              // — это разные утверждения, и второе неправда.
              <div className="flex flex-col gap-2 p-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : tree.groups.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">
                Кластеров и хозяйств не нашлось.
              </p>
            ) : (
              tree.groups.map((g) => {
                const clusterId = g.cluster?.scope_id ?? null;
                const clusterOn = clusterId ? checkedClusters.has(clusterId) : false;
                return (
                  <div key={clusterId ?? 'orphans'} className="py-1">
                    {g.cluster && clusterId && (
                      <label className="flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent">
                        <Checkbox
                          checked={clusterOn}
                          disabled={disabled}
                          onCheckedChange={(v) => toggle('cluster', clusterId, v === true)}
                          aria-label={`Кластер «${g.cluster.name}»`}
                        />
                        <ScopeIcon type="cluster" className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate font-medium">{g.cluster.name}</span>
                      </label>
                    )}
                    {!g.cluster && (
                      <p className="px-2 py-1 text-xs uppercase tracking-wide text-muted-foreground">
                        Без кластера
                      </p>
                    )}
                    {g.farms.map((f) => (
                      <label
                        key={f.scope_id}
                        className={cn(
                          'flex items-center gap-2 rounded-md py-2 pr-2 text-sm hover:bg-accent',
                          g.cluster ? 'pl-8' : 'pl-2',
                        )}
                      >
                        <Checkbox
                          checked={clusterOn || has(value, 'farm', f.scope_id ?? '')}
                          disabled={disabled || clusterOn}
                          onCheckedChange={(v) => toggle('farm', f.scope_id ?? '', v === true)}
                          aria-label={f.name}
                        />
                        <ScopeIcon type="farm" className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate">{f.name}</span>
                        {clusterOn && (
                          <span className="ml-auto text-xs text-muted-foreground">
                            входит в кластер
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                );
              })
            )}
          </div>

          {value.length === 0 && (
            <Label className="text-xs font-normal text-muted-foreground">
              Ничего не выбрано — отметьте хотя бы одну область, иначе запись не
              увидит никто.
            </Label>
          )}
        </div>
      )}
    </div>
  );
}

export default ScopeMembershipPicker;
